<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <page-header
      :title="archived ? 'Archiwum' : 'Produkty'"
      :buttonLabel="archived ? '' : 'Dodaj produkt'"
      :buttonLink="archived ? '' : '/products/new'" />

    <d-row style="width: 100%" class="d-flex ml-0 mb-3 page-filters">
      <form style="width: 100%" class="d-flex">
        <d-input placeholder="Podaj nazwę produktu" v-model="filters.search" />

        <d-button
          theme="success"
          outline
          class="text-nowrap ml-2"
          @click.prevent="searchWithFilters"
          >Szukaj</d-button
        >

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters"
          >Resetuj filtry</d-button
        >
      </form>
    </d-row>

    <d-row v-if="productTypes.length" class="mb-3">
      <!-- Pierwsza linia - Kategorie główne -->
      <d-col cols="12" class="text-center mb-2">
        <d-button
          v-for="(productType, index) of productTypes"
          :key="'product-type-filter-' + index"
          theme="secondary"
          :outline="parseInt(filters.type_id) !== productType.id"
          class="mr-2 mb-2 category-button"
          @click.prevent="toggleTypeFilter(productType.id)"
        >
          {{ productType.name }}
        </d-button>
      </d-col>
      
      <!-- Druga linia - Podkategorie wybranej kategorii -->
      <d-col v-if="selectedTypeChildren.length" cols="12" class="text-center">
        <d-button
          v-for="(child, childIndex) of selectedTypeChildren"
          :key="'subcategory-' + childIndex"
          theme="secondary"
          :outline="!isSubcategorySelected(child.id)"
          class="mr-2 mb-2 subcategory-button"
          @click.prevent="toggleSubcategoryFilter(child.id, filters.type_id)"
        >
          {{ child.name }}
        </d-button>
      </d-col>
    </d-row>

    <d-card style="width: 100%">
      <table class="table mt-0 mb-0 full-width table-hover">
        <thead class="thead-dark">
          <tr>
            <sortable-table-heading
              field="name"
              view="products"
              :column="orderColumn"
              :direction="orderDirection"
              >Nazwa produktu
            </sortable-table-heading>
            <sortable-table-heading
              field="project_name"
              view="products"
              :column="orderColumn"
              :direction="orderDirection"
              >Nazwa projektu
            </sortable-table-heading>
            <sortable-table-heading
              field="created_at"
              view="products"
              :column="orderColumn"
              :direction="orderDirection"
              >Data utworzenia
            </sortable-table-heading>
            <sortable-table-heading
              field="updated_at"
              view="products"
              :column="orderColumn"
              :direction="orderDirection"
              >Data modyfikacji
            </sortable-table-heading>
            <th scope="col" class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr v-for="(product, index) of filteredProducts" :key="'product-' + product.id">
            <td class="align-middle">
              <router-link :to="'/products/' + product.id">
                {{ product.name }}
              </router-link>
              <span 
                v-if="product.status"
                class="status-badge mx-2"
                :style="{ backgroundColor: product.status.color }">
                {{ product.status.name }}
              </span>
            </td>
            <td class="align-middle">
              <template v-if="product.project_name"
                ><small>{{ product.project_name }}</small></template
              >
              <template v-else><em class="text-muted"></em></template>
            </td>
            <td class="align-middle text-center">
              <template v-if="product.created_at"
                ><small>{{
                  formatCreatedAt(product.created_at)
                }}</small></template
              >
              <template v-else><em class="text-muted">Brak</em></template>
            </td>
            <td class="align-middle text-center">
              <template v-if="product.updated_at"
                ><small class="text-center">{{
                  formatUpdatedAt(product.updated_at)
                }}</small></template
              >
              <template v-else><em class="text-muted">Brak</em></template>
            </td>
            <td class="align-middle text-center" style="justify-content: space-around; display: flex;">
              <d-button
                theme="warning"
                @click.prevent="showDuplicateModal(product)"
                title="Duplikuj"
              >
                <i class="material-icons" style="color:white">content_copy</i>
              </d-button>
              <d-button
                theme="dark"
                @click.native="$router.push('/products/' + product.id)"
                title="Edytuj"
              >
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="!archived"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeProduct(product.id)"
                title="Usuń"
              >
                <i class="material-icons">delete</i>
              </d-button>
              <d-button
                v-else
                theme="success"
                :disabled="removeInProgress"
                @click.native="restoreProduct(product.id)"
                title="Przywróć">
                <i class="material-icons">restore_from_trash</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="filteredProducts.length === 0">
            <td colspan="3" class="text-center">
              Brak produktów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert v-if="loadError" show theme="warning">
        Wczytywanie danych nie powiodło się.
        <a href="javascript:window.location.reload();" class="alert-link"
          >Odśwież stronę</a
        >
        aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from "lodash.debounce";
import HasFilters from "./../mixins/HasFilters.vue";
import HasPagination from "./../mixins/HasPagination.vue";
import FormUtils from "./../utils/FormUtils.js";
import ListUtils from "./../utils/ListUtils.js";
import Pagination from "./../components/common/Pagination.vue";

export default {
  name: 'products',
  props: {
    archived: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    pagination: Pagination,
  },
  computed: {
    allDataLoaded() {
      return this.loaded && this.productTypesLoaded;
    },
    selectedTypeChildren() {
      if (!this.filters.type_id) {
        return [];
      }
      // Normalize type_id to number for proper comparison with backend data
      const typeIdNormalized = parseInt(this.filters.type_id, 10);
      const selectedType = this.productTypes.find(type => type.id === typeIdNormalized);
      return selectedType && selectedType.children ? selectedType.children : [];
    },
    filteredProducts() {
      return (this.products || []).filter(product => product != null);
    },
  },
  watch: {
    "filters.type_id": function () {
      this.debouncedLoadFilteredData();
    },
    "filters.subcategory_id": function () {
      this.debouncedLoadFilteredData();
    },
  },
  data() {
    return {
      products: [],
      productTypes: [],
      loaded: false,
      loadError: false,
      productTypesLoaded: false,
      removeInProgress: false,
      filters: {
        type_id: null,
        subcategory_id: null,
        search: "",
      },
    };
  },
  mounted() {
    this.loadAdditionalData();
    this.loadFilteredData();
  },
  methods: {
    formatCreatedAt(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    },
    showDuplicateModal(product) {
      this.$swal({
        title: "Potwierdzenie duplikacji",
        html:
          "Czy na pewno chcesz zduplikować PRODUKT - <b>" +
          (product.name || "") +
          "</b>?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Duplikuj",
        cancelButtonText: "Anuluj",
        buttonsStyling: true,
      }).then((result) => {
        if (result.isConfirmed) {
          this.duplicateProduct(product.id);
        }
      });
    },
    duplicateProduct(productId) {
      this.$http
        .post("/products/duplicate/" + productId)
        .then((response) => {
          if (response.data.success) {
            this.$swal({
              title: "Sukces",
              text: response.data.message || "Produkt został zduplikowany",
              icon: "success",
            }).then(() => {
              // Odśwież listę produktów bez przekierowania
              this.loadFilteredData();
            });
          } else {
            this.$swal({
              title: "Błąd",
              text: response.data.message || "Nie udało się zduplikować produktu",
              icon: "error",
            });
          }
        })
        .catch((error) => {
          this.$swal({
            title: "Błąd",
            text: "Wystąpił problem podczas duplikowania produktu",
            icon: "error",
          });
          console.error(error);
        });
    },
    formatUpdatedAt(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    isSubcategorySelected(childId) {
      if (!this.filters.subcategory_id) return false;
      return parseInt(this.filters.subcategory_id, 10) === parseInt(childId, 10);
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: "/products/types/items",
        params: {
          pagination: 0,
        },
        outputKey: "productTypes",
        loadedKey: "productTypesLoaded",
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.emit("view-filters-save");
      this.loadFilteredData();
    }, 250),
    loadFilteredData() {
      let where = {};

      if (this.filters.subcategory_id) {
        // Jeśli wybrano podkategorię, filtruj tylko po niej
        where.subcategory_id = parseInt(this.filters.subcategory_id, 10);
      } else if (this.filters.type_id) {
        // Jeśli wybrano tylko kategorię główną, filtruj po niej
        where.type_id = parseInt(this.filters.type_id, 10);
      }

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.archived) {
        where.archived = 1;
      }

      ListUtils.loadItemsData(this, {
        endpoint: "/products/items?page=" + this.currentPage,
        listField: "products",
        pagination: true,
        params: {
          where,
          order: {
            column: this.orderColumn,
            direction: this.orderDirection,
          },
        },
      });
    },
    removeProduct(productID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: "/products/delete/",
        id: productID,
        successTitle: "Usunięto produkt",
        successText: "Wybrany produkt został usunięty",
        confirmTitle: "Czy na pewno chcesz usunąć ten produkt?",
        confirmText: "Tej operacji nie można cofnąć",
        errorTitle: "Wystąpił błąd",
        errorText: "Usuwanie produktu nie powiodło się. Spróbuj ponownie.",
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
      });
    },
    restoreProduct (productID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/products/restore/',
        id: productID,
        successTitle: 'Przywrócono produkt',
        successText: 'Wybrany produkt został przywrócony z archiwum',
        confirmTitle: 'Czy na pewno chcesz przywrócić produkt?',
        confirmText: 'Produkt pojawi się z powrotem na liście aktywnych',
        confirmButtonText: 'Tak, przywróć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Przywracanie produktu nie powiodło się.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    toggleTypeFilter (typeID) {
      const normalizedTypeID = parseInt(typeID, 10);
      const normalizedCurrentID = this.filters.type_id ? parseInt(this.filters.type_id, 10) : null;
      
      if (normalizedCurrentID === normalizedTypeID && this.filters.subcategory_id === null) {
        // Kliknięto tę samą kategorię - wyłącz filtr
        this.filters.type_id = null;
      } else {
        // Wybrano nową kategorię
        this.filters.type_id = normalizedTypeID;
        this.filters.subcategory_id = null; // Resetuj podkategorię
      }
    },
    toggleSubcategoryFilter (subcategoryID, parentID) {
      // Ensure we're comparing the same types
      const normalizedSubcategoryID = parseInt(subcategoryID, 10);
      const normalizedCurrentID = this.filters.subcategory_id ? parseInt(this.filters.subcategory_id, 10) : null;
      
      if (normalizedCurrentID === normalizedSubcategoryID) {
        // Kliknięto tę samą podkategorię - pokaż całą kategorię główną
        this.filters.subcategory_id = null;
        this.filters.type_id = parseInt(parentID, 10);
      } else {
        // Wybrano nową podkategorię
        this.filters.subcategory_id = normalizedSubcategoryID;
        this.filters.type_id = parseInt(parentID, 10); // Ustaw też kategorię główną
      }
    },
    clearFilters() {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: "",
        type_id: null,
        subcategory_id: null,
      };

      this.loadFilteredData();
      this.$bus.emit("view-filters-reset");
    },
    searchWithFilters() {
      this.$bus.emit("pagination-reset");
      this.debouncedLoadFilteredData();
    },
  },
};
</script>

<style scoped>
.category-button {
  font-weight: 600;
  min-width: 120px;
}

.subcategory-button {
  font-size: 0.9rem;
  padding: 0.375rem 0.75rem;
  min-width: 100px;
}
</style>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  color: #fff;
  font-weight: 500;
  font-size: 11px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  vertical-align: middle;
}
</style>
